import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map } from 'rxjs/operators';

import { AdjustItemSizeShopcart } from '../../../../../shopcart/store/shopcart.actions';
import { AnalyticsCartAction } from '../../../../constants/cart/analytics-cart.constants';
import { AddToCartEvent } from '../../../models/events/add-to-cart.model';
import { ZenitEventName } from '../../../models/zenit-general.model';
import { ZenitEventApiService } from '../../../services/api/zenit-event-api.service';
import { ZenitEventParserService } from '../../../services/parser/zenit-event-parser.service';
import { ZenitEventSuccess, ZenitEventError, ZenitAddToCartEvent } from '../../actions/zenit.actions';

import { ApiSizeInfo, CartItem } from '@api/cart/cart.models';
import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { GiftCardActions } from '@pages/gift-card/store/gift-card.actions';
import { GiftCardSelectors } from '@pages/gift-card/store/gift-card.selector';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import * as AppEventsAnalyticsActions from '@shared/analytics/store/app-events-analytics.actions';
import { AddProductsShopcartCoreSuccess } from '@shared/shopcart-core/store/shopcart-core.actions';

@Injectable()
export class AddToCartEventEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly zenitEventParserService: ZenitEventParserService,
        private readonly zenitEventApiService: ZenitEventApiService
    ) { }

    /** Efectos de control del evento addToCart */

    public EventAddItemsShopcartZenit$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCoreSuccess),
            concatLatestFrom((action) => {
                const eventFromParser = {
                    category_page: EventFrom.parrilla,
                    ficha_bundle: EventFrom.fichaBundle,
                    ficha_producto: EventFrom.productdetail,
                    parrilla: EventFrom.parrilla,
                    recomendador_tallas: EventFrom['size-recommender-component'],
                    wishlist: EventFrom.wishList,
                };
                const eventFrom = action.location === 'related_product' ? action.parentType
                    : eventFromParser[action?.origin || action?.location];

                return this.zenitEventParserService.getEventObject<AddToCartEvent>(
                    ZenitEventName.AddToCart,
                    eventFrom,
                    { items: action.products, relatedsSectionId: action.relatedsSectionId, place: action.place }
                );
            }),
            map(([_, addToCartEvent]) => ZenitAddToCartEvent({ addToCartEvent })),
        );
    });

    public EventEditAddItemsShopcartZenit$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AdjustItemSizeShopcart),
            concatLatestFrom(({ item, size }) => {
                const addedProduct: ApiSizeInfo = {
                    ...item.colors.find((color) => color.id === item.colorId)?.sizes
                        .find((_size) => _size.sku === size.sku),
                };
                delete addedProduct.name;

                return this.zenitEventParserService.getEventObject<AddToCartEvent>(
                    ZenitEventName.AddToCart,
                    EventFrom.shopcart,
                    { items: [{ ...item, ...addedProduct } as unknown as CartItem] }
                    // actionType: GA4AnalyticsAction.modificarProductoCesta,
                );
            }),
            map(([_, addToCartEvent]) => ZenitAddToCartEvent({ addToCartEvent })),
        );
    });

    public EventQuantityAddItemsShopcartZenit$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.ControlCartAnalytics),
            filter((action) => AnalyticsCartAction.incrementar === action.action),
            concatLatestFrom((action) =>
                this.zenitEventParserService.getEventObject<AddToCartEvent>(
                    ZenitEventName.AddToCart,
                    EventFrom.shopcart,
                    {
                        items: [{
                            ...action.product,
                            quantity: Math.abs(action.extraData?.quantity - (action?.product as CartItem)?.quantity),
                        } as CartItem],
                    }
                    // actionType: GA4AnalyticsAction.modificarProductoCesta,
                )),
            map(([_, addToCartEvent]) => ZenitAddToCartEvent({ addToCartEvent })),
        );
    });

    public EventAddToCartGiftcardZenit$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GiftCardActions.AddToShopCartSuccess),
            concatLatestFrom(() => [
                this.store.select(GiftCardSelectors.getCard),
                this.store.select(GiftCardSelectors.getSelectedGiftCardPrice),
            ]),
            concatLatestFrom(([, giftCardData, giftCardPrice]) =>
                this.zenitEventParserService.getEventObject<AddToCartEvent>(
                    ZenitEventName.AddToCart,
                    EventFrom.giftcard,
                    { items: [{ ...giftCardData, ...giftCardPrice }] }
                    // actionType: GA4AnalyticsAction.addToCartGiftcard,
                )),
            map(([_, addToCartEvent]) => ZenitAddToCartEvent({ addToCartEvent })),
        );
    });

    public addToCartEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ZenitAddToCartEvent),
            concatLatestFrom(() => this.store.select(getCmsConfigValue('enabled_zenit'))),
            filter(([_, enabledZenit]) => enabledZenit),
            concatMap(([{ addToCartEvent }]) => this.zenitEventApiService.postEvent$(addToCartEvent, 'add-to-cart.input.v1').pipe(
                map(() => ZenitEventSuccess<AddToCartEvent>()({ eventData: addToCartEvent })),
                catchError((error) => of(ZenitEventError({ error })))
            )),
        );
    });
}
